// components/RichTextToolbar.jsx
import React from "react";

const ICON_SIZE = 18;

// Contenedor común para todos los SVG (mismo viewBox/tamaño)
const Icon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    width={ICON_SIZE}
    height={ICON_SIZE}
    style={{ display: "block" }}
  >
    {children}
  </svg>
);

// Letras centradas
const Txt = ({ children, weight, italic }) => (
  <Icon>
    <text
      x="12"
      y="12"
      textAnchor="middle"
      dominantBaseline="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontSize="16"
      fontWeight={weight}
      fontStyle={italic ? "italic" : undefined}
    >
      {children}
    </text>
  </Icon>
);

// Subrayado consistente
const Underline = () => (
  <Icon>
    <text
      x="12"
      y="11"
      textAnchor="middle"
      dominantBaseline="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontSize="16"
    >
      U
    </text>
    <rect x="7" y="16" width="10" height="2" rx="1" />
  </Icon>
);

// Lista con viñetas
const ListBullets = () => (
  <Icon>
    <circle cx="7" cy="8" r="1.5" />
    <rect x="10" y="7" width="8" height="2" rx="1" />
    <circle cx="7" cy="12" r="1.5" />
    <rect x="10" y="11" width="8" height="2" rx="1" />
    <circle cx="7" cy="16" r="1.5" />
    <rect x="10" y="15" width="8" height="2" rx="1" />
  </Icon>
);

// Alineaciones
const AlignLeft = () => (
  <Icon>
    <rect x="4" y="7" width="12" height="2" rx="1" />
    <rect x="4" y="11" width="8" height="2" rx="1" />
    <rect x="4" y="15" width="10" height="2" rx="1" />
  </Icon>
);

const AlignCenter = () => (
  <Icon>
    <rect x="6" y="7" width="12" height="2" rx="1" />
    <rect x="4" y="11" width="16" height="2" rx="1" />
    <rect x="6" y="15" width="12" height="2" rx="1" />
  </Icon>
);

const AlignRight = () => (
  <Icon>
    <rect x="8" y="7" width="12" height="2" rx="1" />
    <rect x="10" y="11" width="8" height="2" rx="1" />
    <rect x="8" y="15" width="10" height="2" rx="1" />
  </Icon>
);

const AlignJustify = () => (
  <Icon>
    <rect x="4" y="7" width="16" height="2" rx="1" />
    <rect x="4" y="11" width="16" height="2" rx="1" />
    <rect x="4" y="15" width="16" height="2" rx="1" />
  </Icon>
);

export default function RichTextToolbar({ exec }) {
  const [openColor, setOpenColor] = React.useState(false);
  const colorWrapRef = React.useRef(null);

  // Cerrar popover al click fuera
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!openColor) return;
      if (colorWrapRef.current && !colorWrapRef.current.contains(e.target)) {
        setOpenColor(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openColor]);

  const btnStyle = {
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    marginRight: 8,
    lineHeight: 0,
    fontSize: 0,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: 8,
        background: "#F8FAFB",
        position: "relative",
      }}
    >
      {/* A = menú color */}
      <div ref={colorWrapRef} style={{ position: "relative" }}>
        <button
          type="button"
          style={btnStyle}
          aria-label="Color de texto / resaltado"
          title="Color de texto / resaltado"
          onClick={() => setOpenColor((v) => !v)}
        >
          <Txt weight={600}>A</Txt>
        </button>

        {openColor && (
          <div
            style={{
              position: "absolute",
              top: 36,
              left: 0,
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,.12)",
              padding: 10,
              display: "flex",
              gap: 12,
              zIndex: 20,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              Texto
              <input
                type="color"
                onChange={(e) => exec && exec("foreColor", e.target.value)}
                aria-label="Color de texto"
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              Resalte
              <input
                type="color"
                onChange={(e) => exec && exec("hiliteColor", e.target.value)}
                aria-label="Color de resaltado"
              />
            </label>
          </div>
        )}
      </div>

      {/* Formato de texto */}
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("bold")}
        aria-label="Negrita"
        title="Negrita"
      >
        <Txt weight={700}>B</Txt>
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("italic")}
        aria-label="Cursiva"
        title="Cursiva"
      >
        <Txt italic>I</Txt>
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("underline")}
        aria-label="Subrayado"
        title="Subrayado"
      >
        <Underline />
      </button>

      {/* Lista y alineaciones */}
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("insertUnorderedList")}
        aria-label="Lista"
        title="Lista"
      >
        <ListBullets />
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("justifyLeft")}
        aria-label="Alinear a la izquierda"
        title="Alinear a la izquierda"
      >
        <AlignLeft />
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("justifyCenter")}
        aria-label="Centrar"
        title="Centrar"
      >
        <AlignCenter />
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("justifyRight")}
        aria-label="Alinear a la derecha"
        title="Alinear a la derecha"
      >
        <AlignRight />
      </button>
      <button
        type="button"
        style={btnStyle}
        onClick={() => exec("justifyFull")}
        aria-label="Justificar"
        title="Justificar"
      >
        <AlignJustify />
      </button>
    </div>
  );
}
